import ExcelJS from "exceljs";
import { MASTER_ACTION } from "../redux/action";
import {
  formatDateOnlyYmd,
  getTimesheetPeriodRange,
} from "./dateOnly";
import {
  formatMinutesAsHMM,
  parseItemHoursToMinutes,
} from "./timesheetHelpers";

const DATA_ROW_COUNT = 200;
const SUB_ACTIVITY_HEADER_START_COL = 4;
const MAX_SUB_ACTIVITY_ROWS = 200;

/** Leave hours allowed in UI and Excel (half / full day). */
export const LEAVE_HOURS_OPTIONS = ["04:00", "08:00"];

export const TIMESHEET_IMPORT_HEADERS = [
  "Date",
  "Billed Type",
  "Client",
  "Business Name",
  "Activity",
  "Sub Activity",
  "Description",
  "Hours",
];

export const TIMESHEET_BILLED_TYPE_OPTIONS = [
  "Billable",
  "Non-billable",
  "Leave",
];

export function formatBilledTypeForImport(raw) {
  const norm = normalizeBilledTypeForExcel(raw);
  if (norm === "leave") return "Leave";
  if (norm === "nonbillable" || norm === "idle") return "Non-billable";
  return "Billable";
}

export function formatClientForImportRow(item) {
  const email = String(
    item?.client_email || item?.client_email_id || "",
  ).trim();
  const name = String(item?.client_name || "").trim();
  if (name && email) return `${name} - ${email}`;
  if (email) return email;
  if (name) return name;
  const clientId = String(item?.client_id || "").trim();
  if (clientId.includes("@")) return clientId;
  return clientId || "";
}

function resolveActivityFieldsForImport(item) {
  const billedNorm = normalizeBilledTypeForExcel(
    item?.billed_type || item?.billing_type || item?.billingType,
  );
  if (billedNorm === "leave") {
    return { activity: "", subActivity: "" };
  }

  const activityId = item?.activity_id;
  const customSub = String(item?.custom_sub_activity || "").trim();
  const activityLabel = String(item?.activity || "").trim();
  const isOthers =
    activityLabel.toLowerCase() === "others" ||
    activityId === "Others" ||
    ((activityId === null ||
      activityId === 0 ||
      activityId === "" ||
      activityId === "0") &&
      !!customSub);

  if (isOthers) {
    return {
      activity: "Others",
      subActivity: customSub || String(item?.sub_activity || "").trim(),
    };
  }

  return {
    activity: activityLabel,
    subActivity:
      String(item?.sub_activity || "").trim() ||
      String(item?.custom_sub_activity || "").trim(),
  };
}

/** True when hours represent 4:00 or 8:00 (Leave-only options). */
export function isAllowedLeaveHoursValue(hoursValue) {
  const minutes = parseItemHoursToMinutes(hoursValue);
  return minutes === 4 * 60 || minutes === 8 * 60;
}

/** Normalize Leave hours to 04:00 / 08:00, or "" if invalid. */
export function normalizeLeaveHoursForImport(hoursValue) {
  const minutes = parseItemHoursToMinutes(hoursValue);
  if (minutes === 4 * 60) return "04:00";
  if (minutes === 8 * 60) return "08:00";
  return "";
}

/** Map API timesheet line item → Excel import template row. */
export function mapTimesheetItemToImportRow(item) {
  const billedType = formatBilledTypeForImport(
    item?.billed_type || item?.billing_type || item?.billingType,
  );
  const isNonBillable = billedType === "Non-billable";
  const isLeave = billedType === "Leave";
  const { activity, subActivity } = resolveActivityFieldsForImport(item);
  const itemMinutes = parseItemHoursToMinutes(item?.hours);
  let hoursLabel = formatMinutesAsHMM(itemMinutes);
  if (isLeave) {
    hoursLabel =
      normalizeLeaveHoursForImport(item?.hours) ||
      (itemMinutes === 4 * 60 ? "04:00" : itemMinutes === 8 * 60 ? "08:00" : hoursLabel);
  }

  return {
    _sortYmd: formatDateOnlyYmd(item?.date) || "",
    Date: formatDateOnlyYmd(item?.date) || "",
    "Billed Type": billedType,
    Client: isNonBillable || isLeave ? "" : formatClientForImportRow(item),
    "Business Name":
      isNonBillable || isLeave
        ? ""
        : String(item?.business_name || "").trim(),
    Activity: isLeave ? "" : activity,
    "Sub Activity": isLeave ? "" : subActivity,
    Description: String(item?.description || "").trim(),
    Hours: hoursLabel,
  };
}

export function normalizeBilledTypeForExcel(val) {
  return String(val || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s_]/g, "");
}

export function getActivityDisplayLabel(act) {
  return String(act?.value || act?.activity || "").trim();
}

export function getActivityCanonicalId(act) {
  return act?.activity_id ?? act?.id;
}

export function isOthersActivityOption(act) {
  const label = getActivityDisplayLabel(act);
  const id = String(getActivityCanonicalId(act) ?? "").trim();
  return (
    label.toLowerCase() === "others" ||
    id.toLowerCase() === "others"
  );
}

/** Excel-safe defined name: letters, numbers, underscore only. */
export function toExcelDefinedName(label) {
  let s = String(label || "")
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  if (!s) s = "Empty";
  if (/^\d/.test(s)) s = `_${s}`;
  return `Act_${s}`.slice(0, 255);
}

/** @deprecated Prefer MATCH/OFFSET lists over INDIRECT defined names. */
export function activityLabelToRangeSuffix(label) {
  return String(label || "")
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function listValidation(formula) {
  return {
    type: "list",
    allowBlank: true,
    formulae: [formula],
  };
}

/** Client / Business Name only when Billed Type is Billable. */
function clientFieldsValidation(row) {
  return {
    type: "custom",
    allowBlank: true,
    formulae: [`$B${row}="Billable"`],
    showErrorMessage: true,
    errorStyle: "stop",
    errorTitle: "Not applicable",
    error:
      "Client and Business Name apply to Billable rows only. Leave them blank for Non-billable or Leave.",
  };
}

/**
 * Sub-activity list for the Activity in column E.
 * Uses MATCH against Lists row-1 headers (avoids illegal defined names like Act_R&D_*).
 */
function subActivityListFormula(row) {
  const headerRow = "Lists!$A$1:$ZZ$1";
  const match = `MATCH($E${row},${headerRow},0)`;
  return `=IF(OR($B${row}="Leave",$E${row}=""),"",OFFSET(Lists!$A$2,0,${match}-1,COUNTA(OFFSET(Lists!$A$2,0,${match}-1,${MAX_SUB_ACTIVITY_ROWS},1)),1))`;
}

function parseStartDateValue(startDate) {
  // Prefer plain YYYY-MM-DD string so Excel does not shift the calendar day
  // via timezone when a local-midnight Date is serialized (e.g. Mon → Sun in IST).
  const ymd = formatDateOnlyYmd(startDate);
  return ymd || String(startDate || "").trim() || "";
}

/** ExcelJS date bounds without local-midnight UTC shift (noon UTC). */
function ymdToExcelJsDate(ymd) {
  const parts = String(ymd || "").split("-").map(Number);
  if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) return null;
  return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12, 0, 0));
}

/**
 * Build sample timesheet workbook with dependent dropdowns:
 * Billed Type → Activity (by billing_type) → Sub Activity (by activity).
 * Leave: no client/activity; Hours limited to 04:00 / 08:00.
 * Activities (e.g. Tech, HR, Finance) come from master activityList.
 * Date column is limited to the selected Mon–Sat week (never Sunday).
 */
export async function buildTimesheetSampleWorkbook({
  activityList = [],
  startDate = "",
  endDate = "",
}) {
  const activities = Array.isArray(activityList) ? activityList : [];

  // Always snap to Mon–Sat week so sample never seeds Sunday / out-of-week dates.
  const week = getTimesheetPeriodRange(startDate || endDate);
  const weekStart = week.startDate || formatDateOnlyYmd(startDate) || "";
  const weekEnd = week.endDate || formatDateOnlyYmd(endDate) || weekStart;

  const wb = new ExcelJS.Workbook();

  // Create the user-facing sheet FIRST so importers that read the first sheet
  // (and some spreadsheet viewers) pick the correct tab.
  const ws = wb.addWorksheet("Timesheet template", {
    views: [{ state: "normal" }],
  });
  const headers = [...TIMESHEET_IMPORT_HEADERS];
  ws.getRow(1).values = headers;

  const dateCell = ws.getCell(2, 1);
  dateCell.value = parseStartDateValue(weekStart);
  ws.getCell(2, 2).value = "Billable";
  // Real Excel time (1 hour) — string "01:00" with [h]:mm can corrupt the file.
  ws.getCell(2, 8).value = 1 / 24;
  ws.getCell(2, 8).numFmt = "[h]:mm";

  const billableLabels = [];
  const nonBillableLabels = [];
  const seenActivityLabels = new Set();

  activities.forEach((act) => {
    if (isOthersActivityOption(act)) return;
    const label = getActivityDisplayLabel(act);
    if (!label || seenActivityLabels.has(label)) return;
    seenActivityLabels.add(label);
    const bt = normalizeBilledTypeForExcel(
      act.billing_type || act.billed_type || act.billingType,
    );
    // Leave has no activities; everything else maps to Billable / Non-billable lists
    // (includes Tech, HR, Finance, Idle, etc. from master data).
    if (bt === "nonbillable" || bt === "leave") {
      nonBillableLabels.push(label);
    } else {
      billableLabels.push(label);
    }
  });

  const subListsByActivity = [];
  // Fetch sub-activities once to avoid N API calls
  const allSubs = await MASTER_ACTION.getSubActivity();
  const subsByActivity = {};
  if (Array.isArray(allSubs)) {
    allSubs.forEach((s) => {
      const actId = s.activity_id ?? s.activityId ?? s.activityID;
      if (!actId) return;
      const key = String(actId);
      if (!subsByActivity[key]) subsByActivity[key] = [];
      const label = String(s.sub_activity || s.value || "").trim();
      if (label) subsByActivity[key].push(label);
    });
  }

  const usedHeaders = new Set();
  for (const act of activities) {
    if (isOthersActivityOption(act)) continue;
    const id = getActivityCanonicalId(act);
    const label = getActivityDisplayLabel(act);
    if (!id || !label || usedHeaders.has(label)) continue;
    usedHeaders.add(label);
    const subLabels = (subsByActivity[String(id)] || []).filter(Boolean);
    subListsByActivity.push({
      label,
      subLabels,
    });
  }

  const lists = wb.addWorksheet("Lists");
  lists.getCell(1, 1).value = "Billable Activities";
  lists.getCell(1, 2).value = "Non-billable Activities";
  lists.getCell(1, 3).value = "Leave Hours";

  billableLabels.forEach((label, i) => {
    lists.getCell(i + 2, 1).value = label;
  });
  nonBillableLabels.forEach((label, i) => {
    lists.getCell(i + 2, 2).value = label;
  });
  LEAVE_HOURS_OPTIONS.forEach((label, i) => {
    lists.getCell(i + 2, 3).value = label;
  });

  const billableEnd = Math.max(2, billableLabels.length + 1);
  const nonBillableEnd = Math.max(2, nonBillableLabels.length + 1);
  // ExcelJS API: definedNames.add(location, name) — location first, name second.
  wb.definedNames.add(`Lists!$A$2:$A$${billableEnd}`, "BillableActivities");
  wb.definedNames.add(
    `Lists!$B$2:$B$${nonBillableEnd}`,
    "NonBillableActivities",
  );
  wb.definedNames.add("Lists!$C$2:$C$3", "LeaveHours");

  let subCol = SUB_ACTIVITY_HEADER_START_COL;
  for (const { label, subLabels } of subListsByActivity) {
    lists.getCell(1, subCol).value = label;
    const rows = subLabels.length ? subLabels : [""];
    rows.forEach((sub, i) => {
      lists.getCell(i + 2, subCol).value = sub;
    });
    subCol += 1;
  }

  lists.state = "veryHidden";

  const billedTypeList = `"${TIMESHEET_BILLED_TYPE_OPTIONS.join(",")}"`;

  for (let row = 2; row <= DATA_ROW_COUNT + 1; row++) {
    ws.getCell(row, 2).dataValidation = listValidation(billedTypeList);
    ws.getCell(row, 3).dataValidation = clientFieldsValidation(row);
    ws.getCell(row, 4).dataValidation = clientFieldsValidation(row);
    // Activity: Billable / Non-billable lists; blank for Leave
    ws.getCell(row, 5).dataValidation = listValidation(
      `=IF($B${row}="Billable",BillableActivities,IF($B${row}="Non-billable",NonBillableActivities,""))`,
    );
    ws.getCell(row, 6).dataValidation = listValidation(
      subActivityListFormula(row),
    );
    // Hours: Leave → dropdown 04:00 / 08:00; otherwise free entry (empty list)
    ws.getCell(row, 8).dataValidation = {
      type: "list",
      allowBlank: true,
      showErrorMessage: false,
      showInputMessage: true,
      promptTitle: "Hours",
      prompt:
        "Leave: pick 04:00 or 08:00 from the list. Other billed types: type any duration (e.g. 01:30).",
      formulae: [`=IF($B${row}="Leave",LeaveHours,"")`],
    };
  }

  ws.addConditionalFormatting({
    ref: `C2:F${DATA_ROW_COUNT + 1}`,
    rules: [
      {
        type: "expression",
        formulae: ['OR($B2="Non-billable",$B2="Leave")'],
        style: {
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE8E8E8" },
          },
          font: { color: { argb: "FF6B7280" } },
        },
      },
    ],
  });

  return wb;
}
